import type { PlatformOptions } from "../../shared/apollo/OneGraphApolloClientProvider";

import { resolveOneGraphConfig } from '../../shared/apollo/oneGraphConfig';

declare global {
    var graphqlUrl: string | undefined;
    var graphqlIndeedApiKey: string | undefined;
}

/**
 * Resolution order for OneGraph URL and API key:
 *   1. `globalThis.graphqlUrl` / `globalThis.graphqlIndeedApiKey` — host-injected
 *      globals. Matches the convention used by `apps/profile` and `apps/onboarding`,
 *      and the JSF EJS injection added in JSF-628. Preferred for new integrations
 *      (homepage / SERP embed of RNVJ).
 *   2. `window._initialData.viewJobClientSideModel.oneGraphApi…` — when the values
 *      ride along on the server-rendered ViewJob client-side model instead.
 *   3. `_rootProps.preloadedVJData.oneGraphApi…` — legacy embed via
 *      `mountViewJob({ preloadedVJData })` whose model carries the values.
 *   4. `resolveOneGraphConfig(_tareData.stagingLevel)` — resolve in-bundle from the
 *      staging level the host always provides.
 */
export function getPlatformOptions(): PlatformOptions {
    const localOneGraphConfig = resolveOneGraphConfig(globalThis._tareData?.stagingLevel);

    // Walk the fallback chain documented above. The host-injected global is passed in
    // directly because its key (graphqlUrl / graphqlIndeedApiKey) differs from the
    // `oneGraphApi…` field used by the other sources.
    const resolve = (
        hostGlobal: string | undefined,
        field: 'oneGraphApiUrl' | 'oneGraphApiKey',
        localValue: string
    ) =>
        hostGlobal ??
        window._initialData?.viewJobClientSideModel?.[field] ??
        globalThis._rootProps?.preloadedVJData?.[field] ??
        localValue;

    return {
        web: {
            graphqlUrl: resolve(globalThis.graphqlUrl, 'oneGraphApiUrl', localOneGraphConfig.url),
            graphqlApiKey: resolve(globalThis.graphqlIndeedApiKey, 'oneGraphApiKey', localOneGraphConfig.apiKey),
        },
    };
}
